import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X, Trash2, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { getCourseById, updateModule, deleteModule, addLessonToModule, addModuleToCourse, reorderLessons } from '../services/firebase/courses';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const ModuleOverview = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const { darkMode: isDarkMode } = useTheme();
  const isNewModule = !moduleId || moduleId === 'new';

  // Module state
  const [title, setTitle] = useState('');
  const [moduleLength, setModuleLength] = useState('');
  const [editMode, setEditMode] = useState(isNewModule);
  const [backup, setBackup] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lessons, setLessons] = useState([]);

  // Fetch module data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        if (courseData) {
          if (!isNewModule) {
            const module = courseData.modules?.find(m => m.id === moduleId);
            if (module) {
              setTitle(module.title);
              setModuleLength(module.moduleLength || '');
              // Sort lessons by order to ensure proper display
              const sortedLessons = (module.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0));
              setLessons(sortedLessons);
            } else {
              setError('Module not found');
            }
          }
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching module:', err);
        setError('Failed to load module data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, moduleId, isNewModule]);

  const handleEdit = () => {
    setBackup({ title, moduleLength });
    setEditMode(true);
  };

  const handleCancel = () => {
    setTitle(backup.title);
    setModuleLength(backup.moduleLength);
    setEditMode(false);
  };

  const handleSave = async () => {
    try {
      const moduleData = {
        title,
        moduleLength,
        order: 1 // Default order for new modules
      };

      if (isNewModule) {
        // Create new module
        const newModule = await addModuleToCourse(courseId, moduleData);
        navigate(`/crm/course-templates/${courseId}/modules/${newModule.id}`, { replace: true });
      } else {
        // Update existing module
        await updateModule(courseId, moduleId, moduleData);
        setEditMode(false);
      }
      setError(null);
    } catch (err) {
      console.error('Error saving module:', err);
      setError('Failed to save module. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this module? This will also delete all lessons in this module.')) {
      try {
        await deleteModule(courseId, moduleId);
        navigate(`/crm/course-templates/${courseId}`);
      } catch (err) {
        console.error('Error deleting module:', err);
        setError('Failed to delete module. Please try again.');
      }
    }
  };

  const handleAddLesson = async () => {
    try {
      const newLesson = await addLessonToModule(courseId, moduleId, {
        title: 'New Lesson',
        category: 'General',
        description: '',
        status: 'draft',
        order: lessons.length + 1
      });
      
      // Refresh the module data
      const courseData = await getCourseById(courseId);
      const module = courseData.modules?.find(m => m.id === moduleId);
      if (module) {
        setLessons(module.lessons || []);
      }
      
      // Navigate to the new lesson
      navigate(`/crm/course-templates/${courseId}/lessons/${newLesson.id}`);
    } catch (err) {
      console.error('Error adding lesson:', err);
      setError('Failed to add lesson. Please try again.');
    }
  };

  const moveLesson = async (lessonId, direction) => {
    console.log('Moving lesson:', { lessonId, direction, courseId, moduleId });
    
    const currentIndex = lessons.findIndex(lesson => lesson.id === lessonId);
    if (currentIndex === -1) {
      console.error('Lesson not found in current lessons array');
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= lessons.length) {
      console.log('Cannot move lesson - at boundary');
      return;
    }

    try {
      // Create a new array with the reordered lessons
      const newLessons = Array.from(lessons);
      const [movedLesson] = newLessons.splice(currentIndex, 1);
      newLessons.splice(newIndex, 0, movedLesson);

      console.log('New lesson order:', newLessons.map(l => ({ id: l.id, title: l.title, order: l.order })));

      // Update the local state immediately for better UX
      setLessons(newLessons);

      // Extract lesson IDs in the new order
      const lessonIds = newLessons.map(lesson => lesson.id);
      console.log('Lesson IDs in new order:', lessonIds);

      // Update the order in Firebase
      await reorderLessons(courseId, moduleId, lessonIds);
      
      console.log('Successfully reordered lessons');
      setError(null);
    } catch (err) {
      console.error('Error reordering lessons:', err);
      setError('Failed to reorder lessons. Please try again.');
      
      // Revert the local state on error
      const courseData = await getCourseById(courseId);
      const module = courseData.modules?.find(m => m.id === moduleId);
      if (module) {
        const sortedLessons = (module.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setLessons(sortedLessons);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading module data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto">
        {/* Top Row: Back, Title, Edit */}
        <div className="flex items-center justify-between mb-6 gap-2">
          <BackButton onClick={() => navigate(-1)} />
          <h1 className={`text-3xl font-bold tracking-tight text-center flex-1 text-gray-900 dark:text-white`}>{isNewModule ? 'New Module' : title}</h1>
          <div className="flex gap-2 ml-4 flex-shrink-0">
            {!editMode ? (
              <EditButton onClick={handleEdit} />
            ) : (
              <>
                {!isNewModule && (
                  <button onClick={handleDelete} className="flex items-center gap-1 px-4 py-2 rounded-md bg-red-100 text-red-700 font-semibold hover:bg-red-200">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                )}
                <button onClick={handleCancel} className="flex items-center gap-1 px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300">
                  <X className="h-4 w-4" /> Cancel
                </button>
                <SaveButton onClick={handleSave} />
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className={`md:col-span-2 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm p-6`}>
            <h2 className={`text-lg font-semibold mb-4 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Module Details</h2>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
              <input 
                type="text" 
                className={`w-full border rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                readOnly={!editMode} 
              />
            </div>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Module Length</label>
              <input 
                type="text" 
                className={`w-full border rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                value={moduleLength} 
                onChange={e => setModuleLength(e.target.value)} 
                readOnly={!editMode} 
              />
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Lessons</h2>
              <div className="space-y-2">
                {lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className={`flex items-center justify-between p-3 rounded-md transition-all duration-200 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`font-medium flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {lesson.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Move Up Button */}
                      <button
                        onClick={() => moveLesson(lesson.id, 'up')}
                        disabled={index === 0}
                        className={`p-1 rounded transition-colors ${
                          index === 0 
                            ? `${isDarkMode ? 'text-gray-600' : 'text-gray-300'} cursor-not-allowed`
                            : `${isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`
                        }`}
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      
                      {/* Move Down Button */}
                      <button
                        onClick={() => moveLesson(lesson.id, 'down')}
                        disabled={index === lessons.length - 1}
                        className={`p-1 rounded transition-colors ${
                          index === lessons.length - 1 
                            ? `${isDarkMode ? 'text-gray-600' : 'text-gray-300'} cursor-not-allowed`
                            : `${isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`
                        }`}
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      
                      {/* Edit Button */}
                      <button 
                        onClick={() => navigate(`/crm/course-templates/${courseId}/lessons/${lesson.id}`)}
                        className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 ml-2"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button 
                onClick={handleAddLesson}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md hover:bg-gray-200 mt-4 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700'}`}
              >
                <Plus className="h-4 w-4" /> Add Lesson
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleOverview; 